import React from 'react';
import {
    searchTicketAssignees,
    updateTicket,
} from '../../../../../helpers/ticket-helper';

export const useTaskListAssignee = ({ token, setTickets, setError }) => {
    const [updatingAssigneeIds, setUpdatingAssigneeIds] = React.useState([]);
    const [isAssigneeModalOpen, setIsAssigneeModalOpen] = React.useState(false);
    const [assigneeModalTicketId, setAssigneeModalTicketId] = React.useState('');
    const [assigneeSearchTerm, setAssigneeSearchTerm] = React.useState('');
    const [assigneeOptions, setAssigneeOptions] = React.useState([]);
    const [isAssigneeSearching, setIsAssigneeSearching] = React.useState(false);
    const [assigneeModalError, setAssigneeModalError] = React.useState('');

    const setAssigneeLoading = React.useCallback((ticketId, isLoadingRow) => {
        setUpdatingAssigneeIds((previous) => {
            const id = String(ticketId || '');
            if (!id) return previous;

            if (isLoadingRow) {
                if (previous.includes(id)) return previous;
                return [...previous, id];
            }

            return previous.filter((currentId) => currentId !== id);
        });
    }, []);

    const handleAssigneeChange = React.useCallback(async (ticketId, assigneeId) => {
        if (!token || !ticketId) return false;

        setError('');
        setAssigneeLoading(ticketId, true);

        const payload = {
            assignee: assigneeId || undefined,
        };

        const resp = await updateTicket(token, ticketId, payload);
        if (!resp?.success) {
            setError(resp?.message || 'No se pudo actualizar el asignado.');
            setAssigneeLoading(ticketId, false);
            return false;
        }

        setTickets((previous) => previous.map((ticket) => {
            if (String(ticket?._id) !== String(ticketId)) {
                return ticket;
            }
            return resp.data || ticket;
        }));

        setAssigneeLoading(ticketId, false);
        return true;
    }, [token, setError, setAssigneeLoading, setTickets]);

    const openAssigneeModal = React.useCallback((ticketId) => {
        setAssigneeModalTicketId(String(ticketId || ''));
        setAssigneeSearchTerm('');
        setAssigneeOptions([]);
        setAssigneeModalError('');
        setIsAssigneeModalOpen(true);
    }, []);

    const closeAssigneeModal = React.useCallback(() => {
        if (updatingAssigneeIds.includes(String(assigneeModalTicketId))) {
            return;
        }

        setIsAssigneeModalOpen(false);
        setAssigneeModalTicketId('');
        setAssigneeSearchTerm('');
        setAssigneeOptions([]);
        setAssigneeModalError('');
    }, [updatingAssigneeIds, assigneeModalTicketId]);

    const handleSelectAssigneeFromModal = React.useCallback(async (userId) => {
        const ticketId = String(assigneeModalTicketId || '');
        if (!ticketId) {
            return;
        }

        const wasUpdated = await handleAssigneeChange(ticketId, userId);
        if (wasUpdated) {
            closeAssigneeModal();
        }
    }, [assigneeModalTicketId, handleAssigneeChange, closeAssigneeModal]);

    React.useEffect(() => {
        if (!isAssigneeModalOpen) {
            return undefined;
        }

        const normalizedTerm = String(assigneeSearchTerm || '').trim();
        if (normalizedTerm.length < 2) {
            setAssigneeOptions([]);
            setIsAssigneeSearching(false);
            setAssigneeModalError('');
            return undefined;
        }

        let isActive = true;

        const timeout = window.setTimeout(async () => {
            setIsAssigneeSearching(true);
            setAssigneeModalError('');

            const usersResp = await searchTicketAssignees(token, {
                value: normalizedTerm,
                limit: 20,
            });

            if (!isActive) {
                return;
            }

            if (!usersResp?.success) {
                setAssigneeOptions([]);
                setAssigneeModalError(usersResp?.message || 'No se pudo obtener el listado de usuarios.');
                setIsAssigneeSearching(false);
                return;
            }

            setAssigneeOptions(Array.isArray(usersResp.data) ? usersResp.data : []);
            setIsAssigneeSearching(false);
        }, 250);

        return () => {
            isActive = false;
            window.clearTimeout(timeout);
        };
    }, [isAssigneeModalOpen, assigneeSearchTerm, token]);

    return {
        updatingAssigneeIds,
        isAssigneeModalOpen,
        assigneeModalTicketId,
        assigneeSearchTerm,
        assigneeOptions,
        isAssigneeSearching,
        assigneeModalError,
        openAssigneeModal,
        closeAssigneeModal,
        setAssigneeSearchTerm,
        handleSelectAssigneeFromModal,
    };
};